<script setup lang="ts">
import { useUserStore } from '@/stores/modules/user'
import { computed, ref } from 'vue'
import { getFusionPrice, getFusionRate, getPetName } from '../data'
import { Plus } from '@element-plus/icons-vue'
import type { PetType } from '@/types/pet'
import PetInfo from './PetInfo.vue'
import { socketEmitAsync } from '@/utils/web-socket'
import { formatNumberWithCommas } from '@/utils'
import { computedTotalAttrValue, getMaxFightAttrValue } from '../data'
import { QuestionFilled } from '@element-plus/icons-vue'

const userStore = useUserStore()

const petList = computed(() => {
  return userStore.petList.filter((item) => {
    if (item.id === userStore.fightPetId) return false
    if (item.quality === 'antiquity') return false
    if (userStore.outsidePetIdList.includes(item.id)) return false
    if (petInfoA.value?.id === item.id) return false
    if (petInfoB.value?.id === item.id) return false
    if (petInfoA.value?.quality && petInfoA.value?.quality !== item.quality) return false
    return true
  })
})

const petInfo = ref<PetType>()
const petInfoA = ref<PetType>()
const petInfoB = ref<PetType>()

function handleChoice(item: PetType) {
  if (!petInfoA.value) petInfoA.value = item
  else petInfoB.value = item
  petInfo.value = undefined
}
function handleClear() {
  petInfoA.value = undefined
  petInfoB.value = undefined
  useStabilizer.value = false
}

const useStabilizer = ref(false)
function beforeStabilizerChange() {
  if (!petInfoA.value?.quality) {
    ElMessage.warning('请先放入魔物')
    return false
  }
  if (userStore.consumables.fusionStabilizer <= 0) {
    ElMessage.warning('您未拥有稳定剂')
    return false
  }
  return true
}
const minScore = computed(() => {
  if (!petInfoA.value || !petInfoB.value) return '0'
  const scoreA = computedTotalAttrValue(petInfoA.value.fightAttrs)
  const scoreB = computedTotalAttrValue(petInfoB.value.fightAttrs)
  if (useStabilizer.value) return Math.max(scoreA, scoreB).toFixed(1)
  else return Math.min(scoreA, scoreB).toFixed(1)
})

const isSuc = ref(false)
const resPetInfo = ref<PetType>()
const fusionResShow = ref(false)
async function handleFusion() {
  if (!petInfoA.value || !petInfoB.value) return ElMessage.warning('请放入魔物')
  const price = getFusionPrice(petInfoA.value?.quality)
  if (userStore.gold < price) return ElMessage.warning('金币不足')
  ElMessageBox.confirm(
    `<p class="affix">确定进行<span class="info">融合</span>吗？（该操作将导致放入的魔物<span class="shrink">消失</span>${
      useStabilizer.value ? '，并消耗1 <span class="info">融合稳定剂</span>' : ''
    }）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      if (!petInfoA.value || !petInfoB.value) return ElMessage.warning('客户端出错，请重启游戏')
      const rankA = Number(petInfoA.value.monsterId.toString().slice(0, 1))
      const rankB = Number(petInfoB.value.monsterId.toString().slice(0, 1))
      const quality = petInfoA.value.quality
      const maxAttr = Math.max(computedTotalAttrValue(petInfoA.value.fightAttrs), computedTotalAttrValue(petInfoB.value.fightAttrs))
      const maxRank = Math.max(rankA, rankB)
      if (rankA !== rankB && maxAttr > getMaxFightAttrValue(quality, maxRank)) {
        const action = await ElMessageBox.confirm(
          `<p class="affix">本次融合存在<span class="info">不同阶级</span>且<span class="info">最大评分超出低阶级</span>的魔物，融合时可能会导致战斗评分<span class="shrink">下降</span>，确定融合吗？</p>`,
          '提示',
          {
            type: 'warning',
            dangerouslyUseHTMLString: true,
            appendTo: 'body',
            confirmButtonClass: 'is-plain',
          },
        )
        if (action !== 'confirm') return
      }
      const idA = petInfoA.value!.id
      const idB = petInfoB.value!.id
      const res = await socketEmitAsync<{ petInfo: PetType; isSuc: boolean }>({
        type: 'fusionPet',
        data: { idA, idB, useStabilizer: useStabilizer.value },
      })
      userStore.gold -= price

      const petList = userStore.petList
      const indexA = petList.findIndex((item) => item.id === idA)
      petList.splice(indexA, 1)
      const indexB = petList.findIndex((item) => item.id === idB)
      petList.splice(indexB, 1)
      petList.push(res.petInfo)

      if (useStabilizer.value) userStore.consumables.fusionStabilizer--

      resPetInfo.value = res.petInfo
      isSuc.value = res.isSuc
      fusionResShow.value = true

      handleClear()
    })
    .catch(() => {})
}
</script>

<template>
  <div class="pet-fusion">
    <div class="bench border-wrap">
      <h4>
        实验台
        <div class="help-info">
          <el-popover placement="bottom" width="100%" trigger="click">
            <template #reference>
              <el-icon size="20"><QuestionFilled /></el-icon>
            </template>
            <div class="affix">
              <h4>融合说明</h4>
              <p>1. 魔物融合功能为<span class="shrink">消耗</span> 2只同品质魔物合成一个新的魔物。</p>
              <p>2. 融合后的魔物种类将会在放入的魔物之间产生。</p>
              <p>3. 融合时，将重新随机魔物的各项属性，同时，有一定概率提升品质。</p>
              <p>
                4. 当<span class="info">阶级相同</span><b>或</b
                ><span class="info">评分未达上限</span>时，融合后代的战斗评分不会低于（可能等于）放入魔物的<span class="grow">最低评分</span
                >（使用<span class="info">融合稳定剂时</span>，则取放入魔物的<span class="grow">最高评分</span
                >）；同时，外出属性不会低于（可能等于）放入魔物的最高属性值。
              </p>
            </div>
          </el-popover>
        </div>
      </h4>
      <div class="pet-container">
        <el-button type="primary" plain @click="handleClear">
          <span :class="petInfoA?.quality">{{ petInfoA?.monsterId ? getPetName(petInfoA?.monsterId) : '（未放入）' }}</span>
        </el-button>
        <el-icon><Plus /></el-icon>
        <el-button type="primary" plain @click="petInfoB = undefined">
          <span :class="petInfoB?.quality">{{ petInfoB?.monsterId ? getPetName(petInfoB?.monsterId) : '（未放入）' }}</span>
        </el-button>
      </div>
      <div class="res-tips">
        <p>
          <span>
            结果：
            <template v-if="petInfoA && petInfoB">
              <span v-if="petInfoA.monsterId === petInfoB.monsterId">{{ getPetName(petInfoA?.monsterId) }}</span>
              <span v-else>
                <span>{{ getPetName(petInfoA?.monsterId) }}</span>
                <span style="margin: 0 10px">或</span>
                <span>{{ getPetName(petInfoB?.monsterId) }}</span>
              </span>
            </template>
            <template v-else>（请放入魔物）</template>
          </span>
        </p>
        <p>
          <span class="affix">
            品质提升概率：<span>{{ getFusionRate(petInfoA?.quality || 'antiquity') }}%</span>
          </span>
          <span style="display: flex; align-items: center">
            稳定剂（{{ userStore.consumables.fusionStabilizer }}）：<el-switch
              v-model="useStabilizer"
              size="small"
              :before-change="beforeStabilizerChange"
            />
          </span>
        </p>
        <p class="affix">
          <span>花费：{{ formatNumberWithCommas(getFusionPrice(petInfoA?.quality)) }} 金币</span>
          <span
            >后代最低评分：<span :class="{ grow: useStabilizer }">{{ minScore }}</span></span
          >
        </p>

        <div class="btn-wrap">
          <el-button type="warning" plain @click="handleFusion">开始融合</el-button>
        </div>
      </div>
    </div>

    <div class="pet-list">
      <div class="left border-wrap">
        <h4>魔物列表</h4>
        <p style="font-size: 12px; text-align: right; margin: 0 5px 5px 0">💰 {{ formatNumberWithCommas(userStore.gold) }}</p>
        <div class="item-btn-wrap btn-wrap" style="margin-top: 0">
          <el-row :gutter="10">
            <el-col :span="24" v-for="item of petList" :key="item.id">
              <CommonBtn size="small" @done="petInfo = item" :style="{ borderColor: petInfo?.id === item.id ? 'red' : '' }">
                <span :class="item.quality" v-html="getPetName(item.monsterId)"></span>
              </CommonBtn>
            </el-col>
          </el-row>
          <p class="affix" style="text-align: center" v-show="!petList.length">您的背包中不存在<span class="info">同品质</span>的魔物</p>
        </div>
      </div>
      <div class="right border-wrap">
        <h4>魔物信息</h4>
        <template v-if="petInfo">
          <PetInfo :pet-info="petInfo" :show-action="false" />
          <el-button size="small" type="success" plain @click="handleChoice(petInfo)">放入</el-button>
        </template>
      </div>
    </div>

    <el-dialog v-model="fusionResShow" width="100%" style="max-width: 500px">
      <div class="fusion-res">
        <h2 class="affix">
          <span class="grow">{{ isSuc ? '融合大成功' : '融合成功' }}</span>
        </h2>
        <template v-if="resPetInfo">
          <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
            <template #reference>
              <CommonBtn size="small">
                <span :class="resPetInfo.quality">{{ getPetName(resPetInfo.monsterId) }}</span>
              </CommonBtn>
            </template>
            <PetInfo :pet-info="resPetInfo" :show-action="false" />
          </el-popover>
        </template>
      </div>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="fusionResShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.pet-fusion {
  display: flex;
  flex-flow: column nowrap;
  .border-wrap {
    padding: 0 10px 10px 10px;
  }
  .bench {
    margin-bottom: 10px;
    position: relative;
    p {
      font-size: 14px;
      margin: 8px 0;
    }
    .pet-container {
      display: flex;
      align-items: center;
      justify-content: space-between;
      i {
        margin: 0 10px;
      }
      .el-button {
        flex: 1;
      }
    }
    .res-tips {
      p {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
    }
    .help-info {
      position: absolute;
      top: 10px;
      right: 30px;
    }
  }
  .pet-list {
    flex: 1;
    display: flex;
    justify-content: space-between;
    overflow: hidden;
    .left {
      height: 100%;
      overflow: auto;
      display: flex;
      flex-flow: column nowrap;
      padding: 0;
      width: calc(50% - 5px);
    }
    .right {
      height: 100%;
      overflow: auto;
      display: flex;
      flex-flow: column nowrap;
      width: calc(50% - 5px);
    }
  }
  .fusion-res,
  .pet-list {
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
  }
  .btn-wrap {
    max-height: 100%;
    overflow: auto;
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
  .item-btn-wrap {
    & > div {
      margin-bottom: 10px;
    }
  }
}
</style>
